import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { Container, Card, Button, Badge } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import DataTable from '../../components/DataTable';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import { useNotification } from '../../components/Notification/NotificationContext';
import ErrorProcessor from '../../components/ErrorProcessor';
import CardHeaderMore from '../../components/Cards/CardHeaderMore';
import ContractService from './ContractService';

export default function ContractsPage() {
  const navigate = useNavigate();
  const notification = useNotification();
  const [apiErrors, setApiErrors] = useState({});
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { isadmin } = useSelector((state) => state.auth.user);

  const tableColumns = useMemo(() => [
    { Header: 'Nome', accessor: 'name' },
    { Header: 'Tipo', accessor: 'contract_type' },
    { Header: 'Cliente/Empresa', accessor: 'entity_name' },
    { Header: 'Produto', accessor: 'product_name' },
    { Header: 'Valor', accessor: 'value' },
    { Header: 'Status', accessor: 'status_badge' },
    { Header: 'Ações', accessor: 'actions', noSearch: true },
  ], []);

  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    try {
      setIsLoading(true);
      setContracts(await ContractService.getContracts());
    } catch (err) {
      setApiErrors(err);
      setContracts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = async (id) => {
    try {
      setIsLoading(true);
      await ContractService.deleteContract(id);
      notification('Contrato removido com sucesso', 'success');
      setContracts(contracts.filter((contract) => contract.id !== id));
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === 0) return 'R$ 0,00';
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    } catch (error) {
      return 'R$ 0,00';
    }
  };

  const tableData = useMemo(() => contracts.map((item) => {
    const linksAction = [
      {
        onclick: () => navigate(`/contact/${item.id}/edit`),
        faicon: 'faEdit',
        text: 'Atualizar',
      }
    ];
  
    if (isadmin === 1) {
      linksAction.push({
        custom: (
          <ButtonModal
            onConfirm={() => confirmDelete(item.id)}
            message={`Confirma remoção do Contrato ${item.name}?`}
            buttonProps={{ text: 'Remover', faicon: 'faTrash' }}
            textModal={{ title: 'Remover Contrato', textConfirm: 'Remover' }}
            dropdown
          />
        ),
      });
    }
  
    return {
      name: item.name || 'Nome do contrato',
      contract_type: item.contract_type || 'N/A',
      entity_name: item.contract_type === 'Cliente' 
        ? item.customer?.name || 'N/A'
        : item.company?.name || 'N/A',
      product_name: item.product?.name || 'N/A',
      value: formatCurrency(item.value),
      actions: (
        <div className="d-flex justify-content-between">
          <div>
            <span>{item.status ? 'Ativo' : 'Inativo'}</span>
          </div>
          <CardHeaderMore links={linksAction} className="" />
        </div>
      ),
      status_badge: (
        <Badge bg={item.status ? 'success' : 'danger'}>
          {item.status ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
      ...item,
    };
  }), [contracts, navigate, isadmin, confirmDelete]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs title="Contratos" />
      <div className="page-header d-flex justify-content-between align-items-center">
        <h1 className="page-heading">Contratos</h1>
        <Link to="/contact/new">
          <Button variant="primary" className="text-uppercase">
            <FaPlus /> Cadastrar
          </Button>
        </Link>
      </div>
      <section>
        <Card className="card-table mb-5">
          <ErrorProcessor error={apiErrors} />
          <DataTable
            items={tableData}
            columns={tableColumns}
            defaultPageSize={5}
          />
        </Card>
      </section>
    </Container>
  );
}